import React, { useEffect } from "react";
import { withFormik } from "formik";
import moment from "moment";
import { ButtonSizeOptions } from "@loanbossinc/component-library";

import {
  BackButton,
  DealName,
  FinishButtonWrapper,
  ReportName,
  Step3ButtonBar,
  StyledPrimaryButton,
  Title,
  WorkflowLinkContent,
  WorkflowLinkContentDate,
  WorkflowLinkContentTitle,
  WorkflowLinkIcon,
  WorkflowLinkWrapper
} from "./styles";
import CheckmarkIcon from "./checkmarkIcon.svg";
import WarningIcon from "./warningIcon.svg";

const Step3 = props => {
  const {
    deal,
    history,
    selectedReportId,
    values,
    wizardStepDecrement,
    isLoading,
    reportConfig,
    reportComponentsForSelectedGroup,
    getReportComponentsForReportGroup
  } = props;
  const { reportName, shouldCalculateDebtYield, shouldCalculateDscr } = values;

  useEffect(() => {
    getReportComponentsForReportGroup({ reportGroupId: selectedReportId });
  }, [selectedReportId]);

  const handleGoBack = () => {
    wizardStepDecrement();
  };

  const createIcon = compType => {
    if (reportComponentsForSelectedGroup) {
      const isComponentConfigured = Boolean(reportComponentsForSelectedGroup.find(comp => comp.type === compType));

      return <img src={isComponentConfigured ? CheckmarkIcon : WarningIcon} alt={isComponentConfigured ? "Completed" : "Not completed"} />;
    }

    return null;
  };

  const formatDate = date => {
    if (date) {
      return `Last updated on ${moment.utc(date).format("MMMM Do, YYYY")}`;
    }

    return "Needs review";
  };

  const handleNavToNoi = componentType => {
    const reportComponent = reportComponentsForSelectedGroup && reportComponentsForSelectedGroup.find(comp => comp.type === componentType);
    let redirectUrl;
    if (componentType === "NOI") {
      redirectUrl = `/deal/${deal.id}/lender-reports/${selectedReportId}/noi-calculation/${reportComponent?.id || "new"}`;
    }
    if (componentType === "DSCR") {
      redirectUrl = `/deal/${deal.id}/lender-reports-dscr-config/${selectedReportId}`;
    }
    if (componentType === "DebtYield") {
      redirectUrl = `/deal/${deal.id}/lender-reports-debt-yield-config/${selectedReportId}`;
    }
    history.push(redirectUrl, { from: "report-wizard" });
  };

  const canFinish = () => {
    if (!reportComponentsForSelectedGroup) {
      return false;
    }

    const isReportComponentConfigured = type => Boolean(reportComponentsForSelectedGroup.find(rc => rc.type === type));

    const isNoiConfigured = isReportComponentConfigured("NOI");
    const isDYConfigured = isReportComponentConfigured("DebtYield");
    const isDSCRConfigured = isReportComponentConfigured("DSCR");

    return 1 + shouldCalculateDebtYield + shouldCalculateDscr === isNoiConfigured + isDSCRConfigured + isDYConfigured;
  };

  const onFinish = () => {
    history.push(`/deal/${deal.id}/lender-reports/${selectedReportId}`);
  };

  return (
    <>
      <Title>Customize calculations for</Title>
      <DealName>{deal && deal.name}</DealName>
      <ReportName>{reportName}</ReportName>

      {(shouldCalculateDscr || shouldCalculateDebtYield) && (
        <WorkflowLinkWrapper id="noi-button" onClick={() => handleNavToNoi("NOI")}>
          <WorkflowLinkIcon>{!isLoading && createIcon("NOI")}</WorkflowLinkIcon>
          <WorkflowLinkContent>
            <WorkflowLinkContentTitle>Adjusted Net Operating Income</WorkflowLinkContentTitle>
            <WorkflowLinkContentDate>{!isLoading && formatDate(reportConfig?.noiLastUpdatedDate)}</WorkflowLinkContentDate>
          </WorkflowLinkContent>
        </WorkflowLinkWrapper>
      )}

      {shouldCalculateDscr && (
        <WorkflowLinkWrapper id="dscr-button" onClick={() => handleNavToNoi("DSCR")}>
          <WorkflowLinkIcon>{!isLoading && createIcon("DSCR")}</WorkflowLinkIcon>
          <WorkflowLinkContent>
            <WorkflowLinkContentTitle>Debt Service for DSCR</WorkflowLinkContentTitle>
            <WorkflowLinkContentDate>{!isLoading && formatDate(reportConfig?.dscrLastUpdatedDate)}</WorkflowLinkContentDate>
          </WorkflowLinkContent>
        </WorkflowLinkWrapper>
      )}

      {shouldCalculateDebtYield && (
        <WorkflowLinkWrapper id="debt-yield-button" onClick={() => handleNavToNoi("DebtYield")}>
          <WorkflowLinkIcon>{!isLoading && createIcon("DebtYield")}</WorkflowLinkIcon>
          <WorkflowLinkContent>
            <WorkflowLinkContentTitle>Loan Balance for Debt Yield</WorkflowLinkContentTitle>
            <WorkflowLinkContentDate>{!isLoading && formatDate(reportConfig?.debtYieldLastUpdatedDate)}</WorkflowLinkContentDate>
          </WorkflowLinkContent>
        </WorkflowLinkWrapper>
      )}

      <Step3ButtonBar>
        <BackButton onClick={handleGoBack} id="back-button" label="Reporting setup" iconName="arrow_forward" reverse alignRight={false} key={1} />
        <FinishButtonWrapper>
          <StyledPrimaryButton disabled={!canFinish()} size={ButtonSizeOptions.sm} label="Finish" onClick={onFinish} style={{ marginTop: "0" }} />
        </FinishButtonWrapper>
      </Step3ButtonBar>
    </>
  );
};

export default withFormik({
  mapPropsToValues: ({ reportConfig }) => ({ ...reportConfig })
})(Step3);
